/**
 * FlagPanel — flag / unflag an admin match.
 *
 * Permission gate: `matches.flag` (renders nothing if missing).
 * Backend contract:
 *   POST   /v1/admin/matches/:matchId/flag  body { reason }
 *   DELETE /v1/admin/matches/:matchId/flag
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2 / §6.6
 */
import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';

interface MatchSummaryShape {
  match_id: string;
  admin_flagged_at: string | null;
  admin_flag_reason: string | null;
  admin_flagged_by_admin_id?: string | null;
  flag_source?: 'admin' | 'safety_rail' | null;
  safety_review_acked_at?: string | null;
}

interface Props {
  match: MatchSummaryShape;
  onChange?: () => void;
}

export function FlagPanel({ match, onChange }: Props) {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!hasPermission('matches.flag')) return null;
  const isFlagged = !!match.admin_flagged_at;
  const isRail = match.flag_source === 'safety_rail';
  const acked = !!match.safety_review_acked_at;

  const flag = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await adminFetch(`${apiUrl}/v1/admin/matches/${match.match_id}/flag`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      });
      setReason('');
      onChange?.();
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Failed to flag match');
    } finally {
      setBusy(false);
    }
  };

  const unflag = async () => {
    if (!window.confirm(isRail
      ? 'Acknowledge this safety-rail flag? This only clears the queue — it does not present the match.'
      : 'Clear the flag on this match?')) return;
    setBusy(true);
    setErr(null);
    try {
      await adminFetch(`${apiUrl}/v1/admin/matches/${match.match_id}/flag`, {
        method: 'DELETE',
      });
      onChange?.();
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Failed to unflag match');
    } finally {
      setBusy(false);
    }
  };

  if (isFlagged) {
    return (
      <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-[13px] text-amber-600 dark:text-amber-400">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>
            ⚑ {isRail ? 'Safety rail' : 'Flagged'}: <strong>{match.admin_flag_reason}</strong>
            {isRail && acked ? ' (acked)' : ''}
          </span>
          {!(isRail && acked) && (
            <button
              onClick={unflag}
              disabled={busy}
              type="button"
              className={inlineButtonClass}
            >
              {busy ? '…' : isRail ? 'Ack' : 'Unflag'}
            </button>
          )}
        </div>
        {err && <div className={errClass}>{err}</div>}
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border bg-muted/50 px-2.5 py-2 text-[13px]">
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Flag reason (required)"
          aria-label="Flag reason"
          className="flex-1 rounded border bg-background px-2 py-1.5 text-[13px] text-foreground placeholder:text-muted-foreground"
        />
        <button
          onClick={flag}
          disabled={busy || !reason.trim()}
          type="button"
          className={inlineButtonClass}
        >
          {busy ? '…' : '⚑ Flag'}
        </button>
      </div>
      {err && <div className={errClass}>{err}</div>}
    </div>
  );
}

const inlineButtonClass =
  'cursor-pointer rounded border bg-background px-2.5 py-1.5 text-[13px] hover:bg-muted';

const errClass = 'mt-1.5 text-xs text-destructive';
